import React, { useState, useEffect } from 'react';
import apiClient from '@/services/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const TYPE_LABELS = {
  LOYALTY_ADD: { label: 'Points add', color: 'bg-amber-100 text-amber-800' },
  LOYALTY_DEDUCT: { label: 'Points deduct', color: 'bg-amber-100 text-amber-800' },
  WALLET_ADD: { label: 'Wallet credit', color: 'bg-red-100 text-red-800' },
  WALLET_DEDUCT: { label: 'Wallet clawback', color: 'bg-red-100 text-red-800' },
  SETTINGS_CHANGE: { label: 'Settings change', color: 'bg-indigo-100 text-indigo-800' },
};

const LoyaltyWalletControl = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [processing, setProcessing] = useState(null);

  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    loadRequests();
    loadSettings();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/credit-requests', { params: { status: 'PENDING' } });
      setRequests(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load approval queue');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await apiClient.get('/credit-requests/loyalty-settings');
      setSettings(res.data);
      setForm(res.data);
    } catch (e) {
      toast.error('Failed to load loyalty settings');
    }
  };

  const approve = async (id) => {
    setProcessing(id);
    try {
      await apiClient.put(`/credit-requests/${id}/approve`);
      toast.success('Approved');
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    } finally {
      setProcessing(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setProcessing(rejectTarget.id);
    try {
      await apiClient.put(`/credit-requests/${rejectTarget.id}/reject`, { review_note: rejectNote });
      toast.success('Rejected');
      setRejectTarget(null);
      setRejectNote('');
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rejection failed');
    } finally {
      setProcessing(null);
    }
  };

  const paybackPercent = (rate) => {
    if (!form) return 0;
    return (rate * form.point_value_rupees * 100).toFixed(2);
  };

  const submitSettingsChange = async () => {
    // Diff against last-loaded settings so the change request only carries
    // fields that actually changed.
    const patch = {};
    ['online_points_per_rupee', 'outlet_points_per_rupee', 'point_value_rupees',
     'min_balance_to_redeem', 'max_order_percent_coverable', 'expiry_days',
     'redemption_cooldown_days', 'active'].forEach((k) => {
      if (form[k] !== settings[k]) patch[k] = form[k];
    });
    if (Object.keys(patch).length === 0) return toast.info('No changes to submit');

    setSavingSettings(true);
    try {
      await apiClient.post('/credit-requests', {
        store_id: 'global',
        type: 'SETTINGS_CHANGE',
        reason: 'Loyalty settings update via SuperAdmin control center',
        settings_patch: patch,
      });
      toast.success('Settings change submitted for approval');
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit settings change');
    } finally {
      setSavingSettings(false);
    }
  };

  if (!form) return <div className="text-sm text-gray-400 p-4">Loading...</div>;

  return (
    <div className="space-y-6">
      <Tabs defaultValue="approvals" className="w-full">
        <TabsList>
          <TabsTrigger value="approvals">Approvals Queue ({requests.length})</TabsTrigger>
          <TabsTrigger value="settings">Loyalty Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="approvals">
          <Card>
            <CardHeader><CardTitle className="text-base">Pending requests</CardTitle></CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-gray-400">Loading...</p>
              ) : requests.length === 0 ? (
                <p className="text-sm text-gray-400">No pending requests.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Requested by</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <Badge className={TYPE_LABELS[r.type]?.color}>
                            {TYPE_LABELS[r.type]?.label || r.type}
                          </Badge>
                          {r.source === 'cancellation_photo_request' && (
                            <div className="text-[10px] text-gray-400 mt-1">Order #{r.reference_order_number} · photo evidence</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{r.customer_phone || '— global —'}</TableCell>
                        <TableCell className="text-sm font-medium">
                          {r.type === 'SETTINGS_CHANGE' ? JSON.stringify(r.settings_patch) : r.amount}
                        </TableCell>
                        <TableCell className="text-sm">{r.requested_by_name} · {r.requested_by_role}</TableCell>
                        <TableCell className="text-sm max-w-xs truncate">{r.reason}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" disabled={processing === r.id} onClick={() => approve(r.id)}>
                              Approve
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              disabled={processing === r.id}
                              onClick={() => setRejectTarget(r)}
                            >
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Loyalty settings</CardTitle>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                Program active
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <div className="font-semibold text-sm">Online</div>
                  <div className="text-xs text-gray-400 mb-3">Orders placed via customer app</div>
                  <label className="text-xs text-gray-500">Earn rate (pts / ₹1)</label>
                  <Input
                    type="number" step="0.1" className="mt-1"
                    value={form.online_points_per_rupee}
                    onChange={(e) => setForm({ ...form, online_points_per_rupee: Number(e.target.value) })}
                  />
                  <div className="mt-3 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded p-2 text-center">
                    = {paybackPercent(form.online_points_per_rupee)}% back
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="font-semibold text-sm">Guest at outlet</div>
                  <div className="text-xs text-gray-400 mb-3">POS / Assisted orders</div>
                  <label className="text-xs text-gray-500">Earn rate (pts / ₹1)</label>
                  <Input
                    type="number" step="0.1" className="mt-1"
                    value={form.outlet_points_per_rupee}
                    onChange={(e) => setForm({ ...form, outlet_points_per_rupee: Number(e.target.value) })}
                  />
                  <div className="mt-3 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded p-2 text-center">
                    = {paybackPercent(form.outlet_points_per_rupee)}% back
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Point value (₹)</label>
                  <Input
                    type="number" step="0.01"
                    value={form.point_value_rupees}
                    onChange={(e) => setForm({ ...form, point_value_rupees: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Min balance to redeem</label>
                  <Input
                    type="number"
                    value={form.min_balance_to_redeem}
                    onChange={(e) => setForm({ ...form, min_balance_to_redeem: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Max % of order</label>
                  <Input
                    type="number"
                    value={form.max_order_percent_coverable}
                    onChange={(e) => setForm({ ...form, max_order_percent_coverable: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Expiry (days)</label>
                  <Input
                    type="number"
                    value={form.expiry_days}
                    onChange={(e) => setForm({ ...form, expiry_days: Number(e.target.value) })}
                  />
                </div>
              </div>

              <p className="text-xs text-gray-400">
                Point value is shared across both channels — changing it updates both payback percentages above.
                Settings changes go through the same approval queue as any other adjustment.
              </p>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setForm(settings)}>Reset</Button>
                <Button disabled={savingSettings} onClick={submitSettingsChange}>
                  {savingSettings ? 'Submitting...' : 'Submit for approval'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject request</DialogTitle></DialogHeader>
          <Textarea
            placeholder="Optional note explaining the rejection"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmReject}>Confirm reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoyaltyWalletControl;
